import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Linkedin, Facebook } from 'lucide-react';
import zeynLogo from '@/assets/zeyn-tour-logo.png';
import etbisQr from '@/assets/etbis-karekod.jpeg';
import trLogo from '@/assets/tr-logo.png';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/lib/i18n';
import { getOptimizedImageUrl } from '@/lib/utils';

export function Footer() {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="col-span-1 md:col-span-2 lg:col-span-2">
            <Link 
              to="/" 
              className="flex items-center gap-2 mb-2 w-fit"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img 
                src={zeynLogo} 
                alt="Zeyn Tour" 
                className="h-20 w-auto brightness-0 invert" 
                width="80" 
                height="80" 
                loading="lazy"
                style={{ maxWidth: '80px', height: 'auto' }}
              />
            </Link>
            <p className="text-white/80 mb-4 max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/zeyntour1/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61557242953001" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/zeyntour/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a 
                href="https://www.tiktok.com/@zeyntour1" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                aria-label="TikTok"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://wa.me/905051647998" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                aria-label="WhatsApp"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
              <a 
                href="https://maps.app.goo.gl/Sa1mWLUJELyuM4wb9" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                aria-label="Location"
              >
                <MapPin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <div className="flex flex-col gap-2">
              <Link to="/" className="block text-white/80 hover:text-white transition-colors">
                {t('footer.home')}
              </Link>
              <Link to="/cars" className="block text-white/80 hover:text-white transition-colors">
                {t('footer.ourFleet')}
              </Link>
              <Link to="/services" className="block text-white/80 hover:text-white transition-colors">
                {t('footer.services')}
              </Link>
              <Link to="/campaigns" className="block text-white/80 hover:text-white transition-colors">
                {t('footer.campaigns')}
              </Link>
              <Link to="/contact" className="block text-white/80 hover:text-white transition-colors">
                {t('footer.contact')}
              </Link>
              <Link to="/delivery-form" className="block text-white/80 hover:text-white transition-colors">
                {t('footer.deliveryForm')}
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.contactInfo')}</h3>
            <div className="flex flex-col gap-3">
              <a href="tel:+905051647998" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                <Phone className="h-5 w-5" />
                <div className="text-sm" dir="ltr">
                  <div>+90 505 164 79 98</div>
                </div>
              </a>
              <a href="mailto:info@zeyntour.com" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors break-all">
                <Mail className="h-5 w-5 flex-shrink-0" />
                <div className="text-sm">
                  <div>info@zeyntour.com</div>
                </div>
              </a>
              <a href="https://maps.app.goo.gl/Sa1mWLUJELyuM4wb9" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-white/80 hover:text-white transition-colors">
                <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div>Istanbul, Turkey</div>
                  <div className="text-white/60">{t('footer.serviceInCities')}</div>
                </div>
              </a>
            </div>
          </div>

          {/* Approved e-commerce assurance */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.approvedEcommerce')}</h3>
            <div className="flex gap-2">
              <a 
                href="https://etbis.ticaret.gov.tr/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-white rounded-lg p-2 hover:shadow-lg transition-shadow flex-shrink-0"
              >
                <img 
                  src={etbisQr} 
                  alt="ETBİS'e Kayıtlıdır" 
                  className="h-16 w-16 object-contain" 
                  width="64" 
                  height="64" 
                  loading="lazy"
                />
              </a>
              <a 
                href="https://www.guvendamgasi.org.tr/index.php" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-white rounded-lg p-2 hover:shadow-lg transition-shadow flex-shrink-0"
              >
                <img 
                  src={trLogo} 
                  alt="TR Güven Damgası" 
                  className="h-16 w-16 object-contain" 
                  width="64" 
                  height="64" 
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0">
            <div className="text-white/60 text-sm">
              {t('footer.copyright')}
            </div>
            <a 
              href="https://prebitit.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/30 hover:text-white/40 transition-colors text-xs"
            >
              {t('footer.poweredBy')}
            </a>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy-policy" className="text-white/60 hover:text-white transition-colors">
                {t('footer.privacyPolicy')}
              </Link>
              <Link to="/terms-of-service" className="text-white/60 hover:text-white transition-colors">
                {t('footer.termsOfService')}
              </Link>
              <Link to="/login" className="text-white/60 hover:text-white transition-colors">
                {t('footer.admin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}